import classNames from 'classnames';
import React, { useContext, useEffect, useState, useRef, CSSProperties } from 'react';
import {WebUI} from "../../wui-core/src/index";
import {
    responsiveArray as DEVICE_SIZES,
    Breakpoint,
    responsiveObserve,
    ScreenMap
} from "../../wui-core/src/responsiveObserve";
import RowContext, {RowContextState} from "./RowContext";
import { ColProps, ColSize, ColPropsKey } from './iLayout';
import omit from 'omit.js';

const colPropsKeyArray: ColPropsKey[] = ['xsOffset', 'smOffset', 'mdOffset', 'lgOffset', 'xlOffset', 'xxlOffset', 'xsPush', 'smPush', 'mdPush', 'lgPush', 'xlPush', 'xxlPush', 'xsPull', 'smPull', 'mdPull', 'lgPull', 'xlPull', 'xxlPull'];

function parseFlex(flex: string | number) {
    if (typeof flex === 'number') {
        return `${flex} ${flex} auto`;
    }

    if (/^\d+(\.\d+)?(px|em|rem|%)$/.test(flex)) {
        return `0 0 ${flex}`;
    }

    return flex;
}

const Col: React.FC<ColProps> = ({
    componentClass = 'div',
    ...props
}) => {
    const [screens, setScreens] = useState<ScreenMap>({
        xs: true,
        sm: true,
        md: true,
        lg: true,
        xl: true,
        xxl: true
    });
    const tokenRef = useRef<number | null>(null);
    const context = useContext(RowContext) as RowContextState;

    useEffect(() => {
        let { screens: contextScreens } = context;
        // 未渲染row 组件
        if (!contextScreens) {
            tokenRef.current = responsiveObserve.subscribe((screens: ScreenMap) => {
                setScreens(screens);
            });
        }

        return () => {
            if (tokenRef.current) {
                responsiveObserve.unsubscribe(tokenRef.current);
            }
        };
    }, [context.screens]);

    const {
        className,
        order,
        span,
        offset,
        push,
        pull,
        clsPrefix = '',
        flex,
        style,
        children,
        ...others
    } = props;
    const Component = componentClass;

    return (
        <RowContext.Consumer>
            {({gutter, supportFlexGap, screens: contextScreens}) => {
                let tbClass = {[clsPrefix]: true};
                let tbClassObj = {};
                const finalScreens = contextScreens ?? screens;

                /**
                 * 对传入props做样式转化 渲染screens第一个响应值为true的样式
                 * @type {[type]}
                 */
                for (let i = 0; i < DEVICE_SIZES.length; i++) {
                    const size = DEVICE_SIZES[i];
                    if (finalScreens && finalScreens[size]) {
                        // 处理 'xxl' | 'xl' | 'lg'| 'md'| 'sm'| 'xs'
                        if (props[size] && Object.keys(tbClassObj).length === 0) {
                            let sizeProps: ColSize = {};
                            const propSize = props[size];
                            if (typeof propSize === 'number') {
                                sizeProps.span = propSize;
                            } else if (typeof propSize === 'object') {
                                sizeProps = propSize || {};
                            }
                            // antd
                            tbClassObj = {
                                [`${clsPrefix}-${size}-${sizeProps.span}`]: sizeProps.span !== undefined,
                                [`${clsPrefix}-${size}-order-${sizeProps.order}`]: !!sizeProps.order || sizeProps.order === 0,
                                [`${clsPrefix}-${size}-offset-${sizeProps.offset}`]: !!sizeProps.offset || sizeProps.offset === 0,
                                [`${clsPrefix}-${size}-push-${sizeProps.push}`]: !!sizeProps.push || sizeProps.push === 0,
                                [`${clsPrefix}-${size}-pull-${sizeProps.pull}`]: !!sizeProps.pull || sizeProps.pull === 0,
                            };
                        }
                        // 处理 xsOffset xsPush xsPull ...
                        const popProp = (propSuffix: 'Offset' | 'Push' | 'Pull', modifier: string) => {
                            let propName: ColPropsKey | Breakpoint = `${size}${propSuffix}` as ColPropsKey; // xs xsOffset lgsOffset
                            const propValue = others[propName];
                            if (typeof propValue === 'number' && propValue != undefined && propValue != null && !Object.keys(tbClass).find(cls => cls.indexOf(modifier) > 0)) {
                                tbClass = {
                                    ...tbClass,
                                    [`${clsPrefix}-${size}${modifier}-${propValue}`]: true
                                };
                            }
                        }

                        popProp('Offset', '-offset');
                        popProp('Push', '-push');
                        popProp('Pull', '-pull');
                    }
                }

                const mergedStyle: CSSProperties = {};
                if (gutter && gutter[0] > 0) {
                    const horizontalGutter = gutter[0] / 2;
                    mergedStyle.paddingLeft = horizontalGutter;
                    mergedStyle.paddingRight = horizontalGutter;
                }

                if (gutter && gutter[1] > 0 && !supportFlexGap) {
                    const verticalGutter = gutter[1] / 2;
                    mergedStyle.paddingTop = verticalGutter;
                    mergedStyle.paddingBottom = verticalGutter;
                }
                if (flex) {
                    mergedStyle.flex = parseFlex(flex);
                }

                return (
                    <Component
                        className={classNames({
                            [`${clsPrefix}-${span}`]: span !== undefined,
                            [`${clsPrefix}-order-${order}`]: order,
                            [`${clsPrefix}-offset-${offset}`]: offset,
                            [`${clsPrefix}-push-${push}`]: push,
                            [`${clsPrefix}-pull-${pull}`]: pull,
                        }, {...tbClass, ...tbClassObj}, className)}
                        {...omit(others, [...DEVICE_SIZES, ...colPropsKeyArray])}
                        style={{...mergedStyle, ...style}}
                    >
                        {children}
                    </Component>
                );
            }}
        </RowContext.Consumer>
    );
};

export default WebUI({name: "col"})(Col);